import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Squares2X2Icon,
    UsersIcon,
    CubeIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    ShoppingBagIcon,
    CurrencyRupeeIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import { dashboardAPI, orderAPI, userAPI, visitorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Dashboard = () => {
    const [stats, setStats] = useState({
        revenue: 0,
        products: 0,
        orders: 0,
        users: 0,
        visitors: 0
    });
    const [loading, setLoading] = useState(true);

    const adminLinks = [
        { name: 'Categories', href: '/admin/categories', icon: Squares2X2Icon, color: 'bg-blue-500' },
        { name: 'Stocks', href: '/admin/stocks', icon: CubeIcon, color: 'bg-green-500' },
        { name: 'Orders', href: '/admin/orders', icon: ShoppingBagIcon, color: 'bg-purple-500' },
        { name: 'Users', href: '/admin/users', icon: UsersIcon, color: 'bg-yellow-500' },
        { name: 'Feedbacks', href: '/admin/feedbacks', icon: ChatBubbleLeftRightIcon, color: 'bg-pink-500' },
        { name: 'Blogs', href: '/admin/blogs', icon: DocumentTextIcon, color: 'bg-indigo-500' },
    ];

    const { user } = useAuth();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Only fetch admin stats when an authenticated admin is present
                if (!user || user.role !== 'admin') {
                    setLoading(false);
                    return;
                }

                // Fetch all data in parallel
                const [revenueRes, productsRes, ordersRes, usersRes, visitorsRes] = await Promise.all([
                    dashboardAPI.getStats(),
                    dashboardAPI.getProductStats(),
                    orderAPI.getAllOrders(),
                    userAPI.adminGetAll(),
                    visitorAPI.getStats()
                ]);

                setStats({
                    revenue: revenueRes.data.totalRevenue || 0,
                    products: productsRes.data.totalProducts || 0,
                    orders: Array.isArray(ordersRes.data) ? ordersRes.data.length : (ordersRes.data?.count || 0),
                    users: Array.isArray(usersRes.data) ? usersRes.data.length : (usersRes.data?.count || 0),
                    visitors: visitorsRes.data.totalVisits || 0
                });
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user]);

    const statCards = [
        { name: 'Total Revenue', value: `₹${stats.revenue.toLocaleString()}`, icon: CurrencyRupeeIcon, color: 'bg-green-600' },
        { name: 'Total Products', value: stats.products, icon: CubeIcon, color: 'bg-blue-600' },
        { name: 'Total Orders', value: stats.orders, icon: ShoppingBagIcon, color: 'bg-purple-600' },
        { name: 'Total Users', value: stats.users, icon: UsersIcon, color: 'bg-yellow-600' },
        { name: 'Total Visitors', value: stats.visitors, icon: EyeIcon, color: 'bg-pink-600' },
    ];

    return (
        <div className="min-h-screen bg-gray-100 p-4 pt-24 md:p-8 md:pt-28">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {statCards.map((item) => (
                        <div key={item.name} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow duration-300">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <item.icon className={`h-10 w-10 text-white p-2 rounded-md shadow-sm ${item.color}`} aria-hidden="true" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {loading ? '...' : item.value}
                                                </div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">Quick Management</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {adminLinks.map((item) => (
                        <Link key={item.name} to={item.href} className="block group">
                            <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow duration-300">
                                <div className="p-5 flex items-center">
                                    <div className={`flex-shrink-0 rounded-md p-3 ${item.color}`}>
                                        <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-lg font-semibold text-gray-900">Manage</div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-5 py-3">
                                    <div className="text-sm">
                                        <span className="font-medium text-cyan-700 group-hover:text-cyan-900">
                                            View {item.name}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
